import React, { useState } from "react";
import PropTypes from "prop-types";
import icon from "../../../../assets/icons/icon.png";
import useInitialConfigStore from "../../../../storeModules/useInitialConfigStore";
import useBotControlStore from "../../../../storeModules/useBotControlStore";
import steps from "../../stepModules/steps";
import BotInputs from "../../../../components/bot-inputs/index";
import { sanitizeHtml } from "../../../../utils/sanitize";

// Utility functions
const shouldShowBubble = (msg) => {
  return msg.text || msg.component || msg.loading;
};

const shouldShowLogo = (messages, index) => {
  return (
    index === messages.length - 1 ||
    messages[index + 1]?.sender !== messages[index]?.sender
  );
};

const getBubbleStyle = (msg, hostBubbles, guestBubbles) => ({
  backgroundColor:
    msg.sender === "bot"
      ? hostBubbles.backgroundColor
      : guestBubbles.backgroundColor,
  color: msg.sender === "bot" ? hostBubbles.color : guestBubbles.color,
});

// Sub-components
const MessageTooltip = ({ msg, hasToShowTitle }) => {
  if (!msg.title || !hasToShowTitle) return null;

  return <span className={`message-tooltip ${msg.sender}`}>{msg.title}</span>;
};

MessageTooltip.propTypes = {
  msg: PropTypes.shape({
    title: PropTypes.string,
    sender: PropTypes.string.isRequired,
  }).isRequired,
  hasToShowTitle: PropTypes.bool.isRequired,
};

const BotLogo = ({ msg, showLogo }) => (
  <div
    className={`bot-logo ${msg.sender} ${
      msg.sender === "bot" && showLogo ? "last-logo" : "space"
    }`}
  >
    {msg.sender === "bot" && showLogo && (
      <img src={icon} className="icon" alt="Logo" />
    )}
  </div>
);

BotLogo.propTypes = {
  msg: PropTypes.shape({
    sender: PropTypes.string.isRequired,
  }).isRequired,
  showLogo: PropTypes.bool.isRequired,
};

const MessageBubble = ({ msg, bubbleStyle, index }) => {
  if (!msg.loading && !msg.text) return null;

  return (
    <div
      className={`message-bubble ${msg.sender}`}
      style={bubbleStyle}
      data-testid={`message-bubble-${msg.sender}-${index}`}
    >
      {msg.loading && (
        <div className="message-bubble--component">{msg.loading}</div>
      )}
      {msg.text && (
        <p
          className="message-ellipsis"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(msg.text) }}
          title={msg.title ? msg.title : ""}
        />
      )}
    </div>
  );
};

MessageBubble.propTypes = {
  msg: PropTypes.shape({
    loading: PropTypes.node,
    text: PropTypes.string,
    title: PropTypes.string,
    sender: PropTypes.string.isRequired,
  }).isRequired,
  bubbleStyle: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
};

const ComponentBubble = ({ msg, currentStep, bubbleStyle }) => {
  const shouldShowComponent =
    (msg.component &&
      steps[currentStep].component &&
      msg.name === steps[currentStep].name) ||
    msg.shouldPersistComponent;

  if (!shouldShowComponent) return null;

  return (
    <div
      style={bubbleStyle}
      className={`message-bubble message-bubble-component--container ${msg.sender}`}
    >
      <div className="message-bubble--component">{msg.component}</div>
    </div>
  );
};

ComponentBubble.propTypes = {
  msg: PropTypes.shape({
    component: PropTypes.node,
    sender: PropTypes.string.isRequired,
    name: PropTypes.string,
  }).isRequired,
  currentStep: PropTypes.number.isRequired,
  bubbleStyle: PropTypes.object.isRequired,
};

const MessageButtons = ({ msg, currentStep }) => {
  const shouldShowButtons =
    msg.buttons && msg.buttons.length > 0 && steps[currentStep].buttons;

  if (!shouldShowButtons) return null;

  return (
    <div className="message-buttons">
      {msg.buttons.map((button, index) => {
        const BotButton = button;
        return BotButton;
      })}
    </div>
  );
};

MessageButtons.propTypes = {
  msg: PropTypes.shape({
    buttons: PropTypes.array,
  }).isRequired,
  currentStep: PropTypes.number.isRequired,
};

const SingleMessage = ({
  msg,
  index,
  messages,
  currentStep,
  hostBubbles,
  guestBubbles,
  hasToShowTitle,
  setHasToShowTitle,
}) => {
  const showLogo = shouldShowLogo(messages, index);
  const bubbleStyle = getBubbleStyle(msg, hostBubbles, guestBubbles);

  const handleTitleClick = () => {
    if (!msg.title) return;

    setHasToShowTitle(true);
    setTimeout(() => {
      setHasToShowTitle(false);
    }, 3000);
  };

  return (
    <div
      onClick={handleTitleClick}
      className={`message-row ${msg.sender}`}
      data-testid={`message-row-${index}`}
      role="button"
      tabIndex="0"
    >
      <MessageTooltip msg={msg} hasToShowTitle={hasToShowTitle} />

      <BotLogo msg={msg} showLogo={showLogo} />

      <MessageBubble msg={msg} bubbleStyle={bubbleStyle} index={index} />

      <ComponentBubble
        msg={msg}
        currentStep={currentStep}
        bubbleStyle={bubbleStyle}
      />

      <MessageButtons msg={msg} currentStep={currentStep} />
    </div>
  );
};

SingleMessage.propTypes = {
  msg: PropTypes.shape({
    sender: PropTypes.string.isRequired,
    title: PropTypes.string,
    text: PropTypes.string,
    loading: PropTypes.node,
    component: PropTypes.node,
    name: PropTypes.string,
    buttons: PropTypes.array,
  }).isRequired,
  index: PropTypes.number.isRequired,
  messages: PropTypes.array.isRequired,
  currentStep: PropTypes.number.isRequired,
  hostBubbles: PropTypes.object.isRequired,
  guestBubbles: PropTypes.object.isRequired,
  hasToShowTitle: PropTypes.bool.isRequired,
  setHasToShowTitle: PropTypes.func.isRequired,
};

const BotMessages = ({ messagesEndRef }) => {
  const [hasToShowTitle, setHasToShowTitle] = useState(false);

  const { hostBubbles, guestBubbles } = useInitialConfigStore.getState(
    (state) => state,
  ).theme;

  const { messages, currentStep, isBotLoading } = useBotControlStore(
    (state) => state,
  );

  return (
    <main className="messages">
      {messages.map((msg, index) => {
        if (!shouldShowBubble(msg)) return null;

        const messageKey = `${msg.sender}-${index}`;

        return (
          <SingleMessage
            key={messageKey}
            msg={msg}
            index={index}
            messages={messages}
            currentStep={currentStep}
            hostBubbles={hostBubbles}
            guestBubbles={guestBubbles}
            hasToShowTitle={hasToShowTitle}
            setHasToShowTitle={setHasToShowTitle}
          />
        );
      })}

      {steps[currentStep]?.inputType && !isBotLoading && (
        <div className="input-area">
          <BotInputs
            inputType={steps[currentStep].inputType}
            submitAction={steps[currentStep].action}
          />
        </div>
      )}

      <div ref={messagesEndRef} id="messages-ref" data-testid="messages-ref" />
    </main>
  );
};

BotMessages.propTypes = {
  messagesEndRef: PropTypes.object.isRequired,
};

export default BotMessages;
